import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter, OnDestroy, ViewChild, ElementRef, AfterViewChecked, AfterViewInit, ChangeDetectorRef, OnChanges, SimpleChanges
} from '@angular/core';
import {ChatbotMessagePair, ChatbotMessagePairCreate} from '../state/chatbot-message-pair';
import {CurrentUser} from '../../user/current-user.service';
import {TranslatorService} from '../../translation/translator.service';
import {ChatbotMessagePairService} from '../api-services/chatbot-message-pair.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Clipboard} from '@angular/cdk/clipboard';
import { trigger, state, style, animate, transition } from '@angular/animations';
import {ChatbotProfileProviderService} from '../services/chatbot-profile-provider.service';

@UntilDestroy()
@Component({
  selector: 'app-chatbot-chat-message',
  templateUrl: './chatbot-chat-message.component.html',
  styleUrls: ['./chatbot-chat-message.component.scss'],
  animations: [
    trigger('changeIcon', [
      state('initial', style({
        backgroundImage: 'url("assets/icons/ID37_Chatbot_Clipboard_Outline.svg")'
      })),
      state('copied', style({
        backgroundImage: 'url("assets/icons/ID37_Chatbot_Clipboard_Filled.svg")'
      })),
      transition('initial => copied', animate('2000ms')),
      transition('copied => initial', animate('2000ms'))
    ])
  ]
})
export class ChatbotChatMessageComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked, OnChanges {

  @Input()
    chatQuestion: ChatbotMessagePairCreate;

  @Input()
    chatMessage: ChatbotMessagePair;

  @Input()
    question: boolean = false;

  @Input()
    enableAutoscroll: boolean = false;

  @Output()
    update: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('container', {static: false})
    container: ElementRef;

  displayName: string = '';

  additionalFeedbackDialog: boolean = false;

  comment: string;

  isCopied: boolean = false;
  copyTimeout: NodeJS.Timeout | null = null;

  markdownContent: string = '';
  questionContent: string = '';
  processedQuestionText: string = '';
  private forceReRenderKey: number = 0;
  private shouldAutoScroll: boolean = true;
  private isUserScrolling: boolean = false;

  constructor(private currentUser: CurrentUser,
              private clipboard: Clipboard,
              private translatorService: TranslatorService,
              private chatbotMessagePairService: ChatbotMessagePairService,
              private chatbotProfileProviderService: ChatbotProfileProviderService,
              private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    if (this.question) {
      this.displayName = this.currentUser.fullName;
    }
    else {
      this.displayName = 'Jay';
    }

    this.initializeContent();

    this.chatbotProfileProviderService.markdownReRender$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.forceMarkdownReRender();
      });

    this.chatbotProfileProviderService.profiles$
      .pipe(untilDestroyed(this))
      .subscribe((profiles) => {
        if (profiles && profiles.length > 0 && this.chatMessage?.answer) {
          setTimeout(() => {
            this.forceMarkdownReRender();
          }, 100);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.chatMessage && changes.chatMessage.currentValue) {
      this.updateMarkdownContent();
    }

    if (changes.chatQuestion && changes.chatQuestion.currentValue) {
      this.updateQuestionContent();
    }
  }

  ngOnDestroy() {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
  }

  ngAfterViewChecked() {
    if (this.enableAutoscroll && this.shouldAutoScroll && !this.isUserScrolling) {
      this.scroll();
    }
  }

  ngAfterViewInit() {
    if (this.enableAutoscroll) {
      this.scroll();
    }
    this.setupScrollListeners();
  }

  scroll() {
    (this.container.nativeElement as HTMLElement).scrollIntoView({block: 'end'});
  }

  private setupScrollListeners(): void {
    if (this.container?.nativeElement) {
      const container = this.container.nativeElement as HTMLElement;
      const parentContainer = container.closest('.chat-container') || document.body;

      let scrollTimeout: NodeJS.Timeout;

      parentContainer.addEventListener('scroll', () => {
        this.isUserScrolling = true;
        this.shouldAutoScroll = false;

        // Reset after user stops scrolling
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.isUserScrolling = false;
          // Only re-enable auto-scroll if user is near the bottom
          const scrollTop = parentContainer.scrollTop;
          const scrollHeight = parentContainer.scrollHeight;
          const clientHeight = parentContainer.clientHeight;

          if (scrollTop + clientHeight >= scrollHeight - 100) {
            this.shouldAutoScroll = true;
          }
        }, 1000);
      });
    }
  }

  rateAnswerPositive(): void {
    if (this.chatMessage.answer_rating === undefined) {
      return;
    }
    if (this.chatMessage.answer_rating === 1) {
      this.chatbotMessagePairService.rateAnswer(this.chatMessage.question_id, 0).subscribe();
      this.chatMessage.answer_rating = 0;
      return;
    }
    else {
      this.chatbotMessagePairService.rateAnswer(this.chatMessage.question_id, 1).subscribe();
      this.chatMessage.answer_rating = 1;
      this.additionalFeedbackDialog = true;
    }
  }

  rateAnswerNegative(): void {
    if (this.chatMessage.answer_rating === undefined) {
      return;
    }
    if (this.chatMessage.answer_rating === -1) {
      this.chatbotMessagePairService.rateAnswer(this.chatMessage.question_id, 0).subscribe();
      this.chatMessage.answer_rating = 0;
      return;
    }
    else {
      this.chatbotMessagePairService.rateAnswer(this.chatMessage.question_id, -1).subscribe();
      this.chatMessage.answer_rating = -1;
      this.additionalFeedbackDialog = true;
    }
  }

  commentAnswer(): void {
    this.chatbotMessagePairService.commentAnswer(this.chatMessage.question_id, this.comment).pipe(untilDestroyed(this)).subscribe();
    this.chatMessage.comment = this.comment;
    this.additionalFeedbackDialog = false;
  }

  copyAnswer(): void {
    this.clipboard.copy(this.chatMessage.answer);

    if (!this.isCopied) {
      this.isCopied = true;

      this.copyTimeout = setTimeout(() => {
        this.isCopied = false;
      }, 1000);
    }
  }

  __(key: string): string {
    return this.translatorService.translate('chatbot-message.' + key);
  }

  private forceMarkdownReRender(): void {
    let hasChanges = false;

    if (this.chatQuestion?.question) {
      const newProcessedText = this.processTextForDisplay(this.chatQuestion.question);
      if (this.processedQuestionText !== newProcessedText) {
        this.processedQuestionText = newProcessedText;
        hasChanges = true;
      }
    }

    if (this.chatMessage?.answer) {
      const rawAnswer = this.chatMessage.answer;
      const newMarkdownContent = this.processTextForDisplay(rawAnswer);
      if (this.markdownContent !== newMarkdownContent) {
        this.markdownContent = newMarkdownContent;
        hasChanges = true;
      }
    }

    if (hasChanges) {
      this.cdr.detectChanges();
    }
  }

  private processTextForDisplay(text: string): string {
    let processedText = this.chatbotProfileProviderService.replaceKeycloakIdsWithNames(text);
    processedText = this.chatbotProfileProviderService.replaceAcronymsWithNames(processedText);
    return processedText;
  }

  private processTextForAcronyms(text: string): string {
    return this.processTextForDisplay(text);
  }

  get processedQuestionContent(): string {
    if (!this.chatQuestion?.question) {
      return '';
    }
    return this.processedQuestionText || this.processTextForAcronyms(this.chatQuestion.question);
  }

  private initializeContent(): void {
    if (this.chatMessage !== undefined) {
      if (this.chatMessage.comment) {
        this.comment = this.chatMessage.comment;
      }
      const rawAnswer = this.chatMessage.answer || '';
      this.markdownContent = this.processTextForDisplay(rawAnswer);
    }

    if (this.chatQuestion?.question) {
      this.questionContent = this.chatQuestion.question;
      this.processedQuestionText = this.processTextForAcronyms(this.chatQuestion.question);
    }
  }

  private updateMarkdownContent(): void {
    if (this.chatMessage?.answer !== undefined) {
      const rawContent = this.chatMessage.answer;
      const processedContent = this.processTextForDisplay(rawContent);

      if (this.markdownContent !== processedContent) {
        this.markdownContent = processedContent;
        this.cdr.detectChanges();
      }
    }
  }

  private updateQuestionContent(): void {
    if (this.chatQuestion?.question) {
      const newQuestionContent = this.chatQuestion.question;
      const newProcessedText = this.processTextForAcronyms(this.chatQuestion.question);

      if (this.questionContent !== newQuestionContent || this.processedQuestionText !== newProcessedText) {
        this.questionContent = newQuestionContent;
        this.processedQuestionText = newProcessedText;
        this.cdr.detectChanges();
      }
    }
  }
}
